import { useState } from "react";
import { motion } from "framer-motion";
import { Building2, MapPin, Globe, Sparkles, ShieldQuestion, ExternalLink, Info, ListChecks, ChevronDown, ChevronUp, CheckCircle2, ShieldCheck } from "lucide-react";

const PROV = {
  VERIFIED: { label: "Verified", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  PUBLIC_SOURCE: { label: "Public source", cls: "text-sky-700 bg-sky-50 border-sky-200" },
  AI_SUGGESTED: { label: "AI-suggested", cls: "text-amber-700 bg-amber-50 border-amber-200" },
  USER_PROVIDED: { label: "You provided", cls: "text-indigo-700 bg-indigo-50 border-indigo-200" },
  USER_CONFIRMED: { label: "You confirmed", cls: "text-indigo-700 bg-indigo-50 border-indigo-200" },
  UNVERIFIED: { label: "Unverified", cls: "text-slate-600 bg-slate-100 border-slate-200" },
  UNABLE_TO_VERIFY: { label: "Unable to verify", cls: "text-slate-600 bg-slate-100 border-slate-200" },
  NOT_FOUND: { label: "Not found", cls: "text-slate-600 bg-slate-100 border-slate-200" },
  NOT_CONFIGURED: { label: "Not configured", cls: "text-slate-600 bg-slate-100 border-slate-200" },
  CONFIRMED: { label: "Confirmed", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  LIKELY: { label: "Likely", cls: "text-sky-700 bg-sky-50 border-sky-200" },
  UNKNOWN: { label: "Unknown", cls: "text-slate-500 bg-slate-100 border-slate-200" },
};
const WEB_STATUS = {
  OFFICIAL_WEBSITE_FOUND: "Official website found", LIKELY_OFFICIAL: "Likely official website",
  POSSIBLE: "Possible website — needs confirmation", NO_WEBSITE_FOUND: "No website found",
  UNABLE_TO_VERIFY: "Unable to verify", USER_PROVIDED: "Provided by owner", NONE: "No website",
};
const CONF = {
  HIGH: "text-emerald-700 bg-emerald-50 border-emerald-200",
  MEDIUM: "text-amber-700 bg-amber-50 border-amber-200",
  LOW: "text-slate-600 bg-slate-100 border-slate-200",
};

const VERIFY_FIELDS = [
  ["name", "Business name"], ["category", "Category"], ["website", "Website"],
  ["address", "Address"], ["phone", "Phone"], ["hours", "Hours"], ["business_status", "Business status"],
];

function fieldValue(field, value) {
  if (value == null || value === "") return null;
  if (field === "hours" && Array.isArray(value)) return value.join(" · ");
  if (field === "location" && typeof value === "object") {
    const lat = value.lat ?? value.latitude, lng = value.lng ?? value.longitude;
    return lat != null && lng != null ? `${lat}, ${lng}` : null;
  }
  return String(value);
}

function Prov({ status }) {
  const p = PROV[status] || PROV.UNVERIFIED;
  return <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${p.cls}`}>{p.label}</span>;
}

function Row({ icon, label, status, children, testid }) {
  const Icon = icon;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0" data-testid={testid}>
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
        <div className="text-sm text-slate-800">{children}</div>
      </div>
      {status && <Prov status={status} />}
    </div>
  );
}

const TIER = {
  VERIFIED: { label: "Verified", cls: "text-emerald-800 bg-emerald-100 border-emerald-300" },
  STRONGLY_CORROBORATED: { label: "Strongly corroborated", cls: "text-teal-800 bg-teal-50 border-teal-300" },
  LIKELY: { label: "Likely", cls: "text-sky-800 bg-sky-50 border-sky-300" },
  UNVERIFIED: { label: "AI-assisted", cls: "text-amber-800 bg-amber-50 border-amber-300" },
  UNKNOWN: { label: "Not found", cls: "text-slate-600 bg-slate-100 border-slate-300" },
};
const SYS_LABEL = { google_places: "Google Places", osm: "OpenStreetMap", ai: "AI discovery", web_search: "Web search", user: "You provided", other: "Other" };
const MATRIX_FIELDS = [["name", "Name"], ["address", "Address"], ["city", "City"], ["state", "State"], ["phone", "Phone"], ["website", "Website"], ["category", "Category"]];

function TierChip({ tier }) {
  const t = TIER[tier] || TIER.UNKNOWN;
  return <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${t.cls}`}>{t.label}</span>;
}

function MiniMatrix({ matrix, testid }) {
  if (!matrix) return null;
  const rows = MATRIX_FIELDS.filter(([k]) => matrix[k] && matrix[k].value != null && matrix[k].value !== "");
  if (!rows.length) return <p className="text-sm text-slate-500">No field-level evidence recorded.</p>;
  return (
    <div className="space-y-2.5" data-testid={testid}>
      {rows.map(([k, label]) => {
        const f = matrix[k];
        return (
          <div key={k} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid={`profile-matrix-${k}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
                <div className="text-sm text-slate-800 break-words">{String(f.value)}</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {f.conflict && <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700">Conflict</span>}
                <TierChip tier={f.tier} />
              </div>
            </div>
            <div className="mt-2 space-y-1 border-t border-slate-100 pt-2">
              {(f.sources || []).map((s, i) => (
                <div key={i} className="flex items-center justify-between gap-2 text-[12px]">
                  <span className="flex items-center gap-1.5 min-w-0 text-slate-500">
                    <CheckCircle2 className={`w-3.5 h-3.5 shrink-0 ${s.supports ? "text-emerald-500" : "text-slate-300"}`} />
                    <span className="truncate">{SYS_LABEL[s.source_system] || s.source}</span>
                  </span>
                  <span className={`truncate max-w-[48%] text-right ${s.supports ? "text-slate-600" : "text-slate-400 line-through"}`}>{String(s.value)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function BusinessProfileCard({ profile }) {
  const [showTrail, setShowTrail] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);
  if (!profile) return null;
  const name = profile.name?.value;
  const loc = profile.location || {};
  const cat = profile.category || {};
  const web = profile.website || {};
  const webUrl = web.value;
  const desc = profile.description?.value;
  const ic = profile.identity_confidence || {};
  const sources = profile.sources || [];
  const trail = profile.trail || [];
  const vf = profile.verified_fields || {};
  const vfields = vf.fields || {};
  const locStr = [loc.city, loc.state].filter(Boolean).join(", ");
  const identity = profile.identity;
  const tier = ic.tier || identity?.confidence_tier;
  const matrix = identity?.primary?.source_matrix;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="business-profile-card">
      <div className="flex items-center justify-between gap-3 flex-wrap pb-4 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-11 h-11 rounded-xl bg-slate-900 text-white"><Building2 className="w-5 h-5" /></span>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-indigo-600 font-bold">Business Profile · Confirmed</div>
            <div className="font-heading text-xl font-bold text-slate-900" data-testid="profile-name">{name}</div>
          </div>
        </div>
        <span data-testid="profile-confidence" className={`rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wide ${CONF[ic.level] || CONF.LOW}`}>
          Identity: {ic.level || "—"}
        </span>
      </div>
      {tier && (
        <div className="pt-3 flex items-center gap-2" data-testid="profile-tier-row">
          <span className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Confidence</span>
          <TierChip tier={tier} />
        </div>
      )}
      <div>
        <Row icon={MapPin} label="Location" status={loc.source} testid="profile-location">{locStr || "—"}</Row>
        <Row icon={MapPin} label="Street address" status={loc.source} testid="profile-address">
          {loc.address || <span className="text-slate-400">Not confirmed in the sources searched</span>}
        </Row>
        <Row icon={Sparkles} label="Business type" status={cat.status} testid="profile-category">
          {cat.display_type || "Unknown"} <span className="text-slate-400">· {cat.industry}</span>
        </Row>
        <Row icon={Globe} label="Website" status={web.verification_status} testid="profile-website">
          <span className="font-medium">{WEB_STATUS[web.status] || web.status}</span>
          {webUrl && <a href={webUrl.startsWith("http") ? webUrl : `https://${webUrl}`} target="_blank" rel="noreferrer" className="ml-2 text-indigo-600 hover:underline inline-flex items-center gap-0.5 break-all">{webUrl} <ExternalLink className="w-3 h-3" /></a>}
        </Row>
        {desc ? <Row icon={Info} label="Description" status={profile.description?.status} testid="profile-description">{desc}</Row> : null}
        {sources.length ? (
          <Row icon={ShieldQuestion} label="Sources" testid="profile-sources">
            <div className="flex flex-wrap gap-1.5 mt-0.5">
              {sources.map((s, i) => <span key={i} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600">{s.name} <Prov status={s.status} /></span>)}
            </div>
          </Row>
        ) : null}
      </div>
      {Object.keys(vfields).length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <button type="button" data-testid="profile-verify-toggle" onClick={() => setShowVerify((v) => !v)}
            className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900">
            <ShieldCheck className="w-4 h-4" /> Verification details {showVerify ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showVerify && (
            <div className="mt-3 grid sm:grid-cols-2 gap-2" data-testid="profile-verify">
              {VERIFY_FIELDS.map(([key, label]) => {
                const f = vfields[key] || {};
                const val = fieldValue(key, f.value);
                return (
                  <div key={key} className="flex items-start justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
                      <div className="text-sm text-slate-800 break-words">{val || <span className="text-slate-400">Not available</span>}</div>
                      {f.checked_at && <div className="text-[10px] text-slate-400 mt-0.5">Last checked {String(f.checked_at).slice(0, 10)}</div>}
                    </div>
                    <Prov status={f.status} />
                  </div>
                );
              })}
              {!vf.provider?.configured && (
                <p className="sm:col-span-2 text-[11px] text-slate-400">Fields like phone, address, hours and business status show <b>Not configured</b> until an authoritative provider (e.g. Google Places) is connected — they are never guessed.</p>
              )}
            </div>
          )}
        </div>
      )}
      {matrix && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <button type="button" data-testid="profile-evidence-toggle" onClick={() => setShowEvidence((v) => !v)}
            className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900">
            <ShieldCheck className="w-4 h-4" /> Identity evidence — source matrix {showEvidence ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showEvidence && (
            <div className="mt-3" data-testid="profile-evidence">
              {identity?.degradation && <p className="text-[12px] text-slate-500 mb-3">{identity.degradation}</p>}
              <MiniMatrix matrix={matrix} testid="profile-evidence-matrix" />
            </div>
          )}
        </div>
      )}
      {trail.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <button type="button" data-testid="profile-trail-toggle" onClick={() => setShowTrail((v) => !v)}
            className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900">
            <ListChecks className="w-4 h-4" /> How we found this business {showTrail ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showTrail && (
            <div className="mt-3 space-y-1.5" data-testid="profile-trail">
              {trail.map((t, i) => {
                const ok = ["VERIFIED", "PUBLIC_SOURCE", "USER_CONFIRMED", "USER_PROVIDED"].includes(t.status);
                return (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${ok ? "text-emerald-500" : "text-slate-300"}`} />
                    <div className="min-w-0 flex-1">
                      <span className="text-slate-700 font-medium">{t.check}</span>
                      {t.detail && <span className="text-slate-400"> — {t.detail}</span>}
                      {t.url && <a href={t.url.startsWith("http") ? t.url : `https://${t.url}`} target="_blank" rel="noreferrer" className="ml-1 text-indigo-600 hover:underline inline-flex items-center gap-0.5 break-all">{t.url} <ExternalLink className="w-3 h-3" /></a>}
                      {t.checked_at && <span className="text-[10px] text-slate-400 ml-1">· checked {String(t.checked_at).slice(0, 10)}</span>}
                    </div>
                    <Prov status={t.status} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {profile.edited && (
        <p className="mt-3 text-[11px] text-slate-400" data-testid="profile-edited-note">Some details were corrected by the owner (recorded as user-confirmed). Originally discovered values are preserved for the record.</p>
      )}
    </motion.div>
  );
}
